import { Clock, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui/card";
import { ScrollArea } from "@repo/ui/scroll-area";
import {
  AuditNotificationRow,
  type NotificationActionType,
} from "@/components/notifications/AuditNotificationRow";
import { fetchAuditNotifications, pauseJobRun, cancelJobRun } from "@/lib/api";
import type { CrawlLogEntry } from "@/lib/api";
import { useLoadMore } from "@/hooks/useLoadMore";
import { useToast } from "@/hooks/use-toast";

const PAGE_SIZE = 20;

export function AuditLogCard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    items,
    totalCount,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
  } = useLoadMore<CrawlLogEntry>({
    fetcher: ({ offset, limit }) => fetchAuditNotifications({ offset, limit }),
    pageSize: PAGE_SIZE,
  });

  const handleNotificationAction = async (action: NotificationActionType, runId: string) => {
    if (action === "view") {
      navigate("/dashboard/jobs");
      return;
    }

    if (action === "pause") {
      const result = await pauseJobRun(runId);
      toast({
        title: result.success ? "Paused" : "Error",
        description: result.success ? "Job has been paused" : result.error,
        variant: result.success ? "default" : "destructive",
      });
      return;
    }

    if (action === "cancel") {
      const result = await cancelJobRun(runId);
      toast({
        title: result.success ? "Cancelled" : "Error",
        description: result.success ? "Job has been cancelled" : result.error,
        variant: result.success ? "default" : "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Audit Log
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {totalCount} notifications
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No notifications yet</p>
          </div>
        ) : (
          <>
            <ScrollArea className="h-[360px]">
              <div className="space-y-2">
                {items.map((item) => (
                  <AuditNotificationRow
                    key={item.id}
                    notification={item}
                    variant="audit"
                    onAction={handleNotificationAction}
                  />
                ))}
              </div>
            </ScrollArea>

            {hasMore && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={loadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Load Older Notifications"
                )}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
